import { useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import type { HomeyApiKind } from '../../lib/HomeyApiKind';
import { selectById, selectData, selectIsLoading } from '../selectors.ts';
import { useAttach } from '../useAttach';

import { devicesStore, type DevicesStoreState } from './DevicesStore';
import { selectDevice } from './DevicesStore.selectors';

type DevicesSelection = ReturnType<typeof selectById<DevicesStoreState>>;

type DevicesSelectionFor<Kind extends HomeyApiKind> = Extract<
  DevicesSelection,
  { apiKind: Kind | null }
>;

type DeviceSelection = ReturnType<typeof selectDevice>;

type DeviceSelectionFor<Kind extends HomeyApiKind> = Extract<
  DeviceSelection,
  { apiKind: Kind | null }
>;

type DevicesOptions<Kind extends HomeyApiKind = HomeyApiKind> = {
  apiKinds: readonly Kind[];
};

// TODO
// lets name this useDevicesInstances
export function useDevicesData() {
  useAttach(devicesStore, 'useDevicesData');

  return useStoreWithEqualityFn(devicesStore.store, selectData, shallow);
}
useDevicesData.store = devicesStore;

export function useDevicesById<Kind extends HomeyApiKind>(
  options: DevicesOptions<Kind>,
): DevicesSelectionFor<Kind>;

export function useDevicesById(options?: { apiKinds?: undefined }): DevicesSelection;

export function useDevicesById(options?: Partial<DevicesOptions>): DevicesSelection {
  useAttach(devicesStore, 'useDevicesById');

  const state = useStoreWithEqualityFn(devicesStore.store, selectById, shallow);
  assertAllowedApiKind(state.apiKind, options?.apiKinds, 'useDevicesById');

  return state;
}
useDevicesById.store = devicesStore;

export function useDevicesSelection({ deviceIds }: { deviceIds: string[] }) {
  useAttach(devicesStore, 'useDevicesSelection');

  const select = useCallback(
    (state: DevicesStoreState) => {
      type Device = NonNullable<DevicesStoreState['byId']>[string];

      const selection: { [key: string]: Device | null } = {};

      for (const deviceId of deviceIds) {
        selection[deviceId] = state.byId?.[deviceId] ?? null;
      }

      return selection;
    },
    [deviceIds],
  );

  return useStoreWithEqualityFn(devicesStore.store, select, shallow);
}
useDevicesSelection.store = devicesStore;

export function useDevice<Kind extends HomeyApiKind>(options: {
  deviceId: string;
  apiKinds: readonly Kind[];
}): DeviceSelectionFor<Kind>;

export function useDevice(options: { deviceId: string; apiKinds?: undefined }): DeviceSelection;

export function useDevice({
  deviceId,
  apiKinds,
}: {
  deviceId: string;
  apiKinds?: readonly HomeyApiKind[];
}): DeviceSelection {
  useAttach(devicesStore, 'useDevice');

  const select = useCallback(
    (state: DevicesStoreState) => {
      return selectDevice(state, deviceId);
    },
    [deviceId],
  );

  const selection = useStoreWithEqualityFn(devicesStore.store, select, shallow);
  assertAllowedApiKind(selection.apiKind, apiKinds, 'useDevice');

  return selection;
}
useDevice.store = devicesStore;

export function useDevicesLoading() {
  useAttach(devicesStore, 'useDevicesLoading');
  return useStoreWithEqualityFn(devicesStore.store, selectIsLoading, Object.is);
}
useDevicesLoading.store = devicesStore;

// TODO
// we can optimize this by making it partial devices that only update when relevant props change.
// something like use ordered device ids by zone id
export function useDevicesByZoneId() {
  useAttach(devicesStore, 'useDevicesByZoneId');

  const devices = useDevicesById();

  const byZoneId = useMemo(() => {
    if (devices.byId == null) return {};

    type Device = NonNullable<DevicesStoreState['byId']>[string];
    const result: Record<string, Record<string, Device>> = {};

    for (const device of Object.values(devices.byId)) {
      result[device.zone] = result[device.zone] ?? {};
      result[device.zone][device.id] = device;
    }

    return result;
  }, [devices.byId]);

  return {
    byZoneId: byZoneId,
    isLoading: devices.isLoading,
    error: devices.error,
  };
}
useDevicesByZoneId.store = devicesStore;

export function useDevicesAttach() {
  useAttach(devicesStore, 'useDevicesAttach');
}
useDevicesAttach.store = devicesStore;

function assertAllowedApiKind(
  apiKind: HomeyApiKind | null,
  allowedApiKinds: readonly HomeyApiKind[] | undefined,
  hookName: string,
) {
  if (apiKind == null || allowedApiKinds == null) {
    return;
  }

  if (allowedApiKinds.includes(apiKind) === false) {
    throw new Error(
      `${hookName} requires Homey API kind ${allowedApiKinds.join(' or ')}; received ${apiKind}`,
    );
  }
}
